import React from 'react';
import styled, { keyframes } from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import useDialogA11y from './dialogA11y';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Sayfaların kendi modal katmanı 1000 kullanıyor (ör. masraf talebi modalı).
     Eşit değerde DOM sırası kazandığı için onay diyaloğu modalın ALTINDA
     kalıyordu — kullanıcı onayı hiç göremiyordu (canlı bulgu 2026-08-28).
     Onay her zaman en üstte olmalı; DocumentViewerModal (2000) dahil. */
  z-index: 2100;
  animation: ${fadeIn} 0.15s ease;
`;

const Dialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
  animation: ${slideUp} 0.2s ease;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Message = styled.p`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 24px;
  line-height: 1.5;
`;

const Extra = styled.div`
  margin: -8px 0 24px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  loading = false,
  type = 'danger',
  // Tek butonlu bilgilendirme kipi: "bu işlem yapılamaz, sebebi şu" diyen bir
  // diyalogda "İptal" anlamsızdır — iptal edilecek bir işlem yoktur.
  hideCancel = false,
  // İsteğe bağlı ek içerik (tik kutusu, sayı girdisi) — mesajla düğmeler arasına
  // çizilir. `confirmDisabled` bu içerik geçersizken onayı kilitler.
  children,
  confirmDisabled = false,
  // ESC / perdeye tıklama. Verilmezse İptal ile aynıdır (bugünkü davranış). İptal düğmesinin
  // YIKICI olduğu diyalogda (hareketsizlik uyarısı: "Çıkış Yap") yanlışlıkla perdeye tıklamak
  // yıkıcı eylemi tetiklememeli — o diyalog bunu güvenli eyleme bağlar.
  onDismiss,
}) {
  const { t } = useTranslation();
  // PARTİ 11 (FE-25) — ESC, odak tuzağı, `role="dialog"`/`aria-modal` ve perde
  // arkasının ekran okuyucudan gizlenmesi. Bu bileşen uygulamadaki 169 dosyada
  // kullanılıyor: tek noktadan yapılan bu ekleme, her silme/onay diyaloğunu
  // klavyeyle kullanılır kılıyor.
  //
  // DIŞARI TIKLAMA DAVRANIŞI DEĞİŞMEDİ (`closeOnOutsideClick: true`): onay
  // diyaloğu kullanıcının YAZDIĞI hiçbir veriyi taşımaz, dolayısıyla perdeye
  // tıklamak burada veri kaybettirmez. Veri taşıyan form diyaloglarında
  // varsayılan bunun TERSİ (kapanmaz) — bkz. dialogA11y karar 1.
  const {
    dialogRef, titleId, dialogProps, overlayHandlers,
  } = useDialogA11y({ open: isOpen, onClose: onDismiss || onCancel, closeOnOutsideClick: true });
  const resolvedConfirmText = confirmText || t('common.confirm');
  const resolvedCancelText = cancelText || t('common.cancel');

  if (!isOpen) return null;

  return (
    <Overlay {...overlayHandlers}>
      <Dialog ref={dialogRef} {...dialogProps}>
        {type === 'danger' && (
          <IconWrapper>
            <AlertTriangle size={24} />
          </IconWrapper>
        )}
        <Title id={titleId}>{title}</Title>
        <Message>{message}</Message>
        {children && <Extra>{children}</Extra>}
        <Actions>
          {!hideCancel && (
            <Button variant="outline" onClick={onCancel} disabled={loading}>{resolvedCancelText}</Button>
          )}
          {/* #72 — yerel DangerButton (`#E53935`/`#0291EF` hardcode + !important)
              kaldırıldı: Button.js'in artık BİREBİR aynı renkleri taşıyan
              `variant="danger"`sı var (bkz. t6-report.md) — bu, ConfirmDialog
              UYGULAMANIN HER YERİNDE kullanıldığı için tek başına en yüksek
              etkili değişikliklerden biri (her silme/onay diyaloğu). */}
          <Button
            variant={type === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={loading || confirmDisabled}
          >
            {loading ? '...' : resolvedConfirmText}
          </Button>
        </Actions>
      </Dialog>
    </Overlay>
  );
}
